import {
  convertLetterCase,
  isDateGreaterThanProvidedYearsAgo,
} from "../../../../utils/Helper";
import { currencyDetails } from "../../../../utils/currency";
import { currencyUnitDetails } from "../../../../utils/CurrencyUnits";

/* Financial Documents — the rules, transcribed. */

/* The live ids, which localStorage's `selectedObject.selectedIndex` holds and
   which the chooser switches on. Numbers, not names — changing them would
   break resume for anyone mid-flow. */
export const METHOD_MANUAL = 1;
export const METHOD_DOCUMENTS = 2;
export const METHOD_CSV = 3;
export const METHOD_GRAPHS = 4;

/* Transcribed titles and descriptions, with the design's own step-3 wording
 * for the bar that names the open method. `graphsOnly` marks the card the live
 * chooser hides unless there is chart data to show.
 */
export const FINANCIAL_METHODS = [
  {
    id: METHOD_MANUAL,
    key: "manual",
    title: "Enter Information Manually",
    description: "Enter all of your details manually from your records.",
    barTitle: "Enter Information Manually",
    barSubtitle: "Fill in figures year by year, from your own records.",
  },
  {
    id: METHOD_DOCUMENTS,
    key: "documents",
    title: "Upload Documents",
    description: "Request TREVEX to fill the details on your behalf.",
    barTitle: "Upload Documents",
    barSubtitle: "We'll read your statements and fill the details for you.",
  },
  {
    id: METHOD_CSV,
    key: "csv",
    title: "Upload CSV File",
    description: "Download our CSV template and fill in the fields.",
    barTitle: "Upload CSV File",
    barSubtitle: "Download the template, fill it, and upload in one go.",
  },
  {
    id: METHOD_GRAPHS,
    key: "graphs",
    title: "Financials Graphs",
    description: "Explore financial trends with interactive graphs.",
    barTitle: "Financials Graphs",
    barSubtitle: "The figures already on record, charted.",
    graphsOnly: true,
  },
];

export const findMethod = (id) =>
  FINANCIAL_METHODS.find((m) => m.id === id) || null;

/* Transcribed gate: every method is locked for a company less than three
 * years in business. The live screen blurs the cards and overlays
 * "<name> is not available … is less than 3 years in business".
 */
export const canUseFinancials = (company) =>
  company?.date_of_establishment
    ? Boolean(isDateGreaterThanProvidedYearsAgo(company.date_of_establishment, 3))
    : false;

/* The live chooser's Proceed is disabled until BOTH are chosen — except for
 * the graphs card, which only reads existing data and needs neither.
 */
export const needsReportingBasis = (methodId) => methodId !== METHOD_GRAPHS;

export const hasReportingBasis = ({ currency, units }) =>
  String(currency ?? "").trim() !== "" && String(units ?? "").trim() !== "";

/* Options for the two selects. The live code stores the currency's NAME
   ("UAE dirham") and shows its CODE ("AED"); the unit stores its name and
   shows it title-cased. Both transcribed. */
export const CURRENCY_OPTIONS = currencyDetails.map((c) => ({
  value: c.name,
  label: c.cc,
}));

export const UNIT_OPTIONS = currencyUnitDetails.map((u) => ({
  value: u.name,
  label: convertLetterCase(u.name, "titlecase"),
}));

/* Resolve a value the SERVER sent onto one of the options above. */
export const matchOptionValue = (options, value) => {
  const want = String(value ?? "").trim().toLowerCase();
  if (!want) return "";
  const hit = (options || []).find(
    (o) => String(o?.value ?? "").trim().toLowerCase() === want
  );
  return hit ? hit.value : "";
};

/* Six years back from this one, newest first — the array every method builds its years from. */
export const financialYears = (now = new Date()) => {
  const current = now.getFullYear();
  return Array.from({ length: 6 }, (_, i) => current - i);
};

/* The FIVE years the manual form offers, and which five depends on whether the current year has been skipped. */
export const manualYears = (hideLastYear = true, now = new Date()) => {
  const all = financialYears(now);
  return hideLastYear ? all.slice(0, 5) : all.slice(1, 6);
};

/* Transcribed: the left button skips the current year when you are on it, and otherwise steps to a NEWER one. */
export const stepBack = (selectedYear, hideLastYear, now = new Date()) => {
  const all = financialYears(now);
  const year =
    all[0] !== selectedYear
      ? selectedYear + 1
      : selectedYear === all[1] && !hideLastYear
      ? selectedYear + 1
      : selectedYear - 1;

  let nextHide = hideLastYear;
  if (all[0] === selectedYear) nextHide = false;
  if (all[1] === selectedYear) nextHide = true;
  return { year, hideLastYear: nextHide };
};

/* Transcribed: the right button reads "Next" until three years are behind you, and "Save & Continue" from then on — `years[0] - (!hideLastYear ? */
export const isSaveStage = (selectedYear, hideLastYear, now = new Date()) => {
  const all = financialYears(now);
  return all[0] - (hideLastYear ? 2 : 3) >= selectedYear;
};

/* Transcribed: a save on the LAST year of the window leaves the section; any
   other save steps to the next older year. */
export const isLastOfWindow = (selectedYear, hideLastYear, now = new Date()) => {
  const all = financialYears(now);
  return selectedYear === (hideLastYear ? all[4] : all[5]);
};

/* Transcribed from UploadDocument's checkYearValidity. */
export const checkYearValidity = (rows, now = new Date()) => {
  const entries = rows || [];
  const current = now.getFullYear();
  const validYears = Array.from({ length: 5 }, (_, i) => current - i);

  const hasCurrent = entries.some((item) => item.year === current);
  const required = hasCurrent
    ? [current - 1, current - 2]
    : [current - 1, current - 2, current - 3];

  const missing = required.filter(
    (year) => !entries.some((item) => item.year === year && item?.document !== "")
  );

  /* Transcribed: the out-of-range check only runs when the current year is
     present, which is the live behaviour however odd it reads. */
  const warning = hasCurrent
    ? entries.some((item) => !validYears.includes(item.year))
    : false;

  return {
    warning,
    missing,
    /* The live wording, both branches. */
    errorMessage: missing.length
      ? hasCurrent
        ? `Please add data for ${missing.join(" and ")}.`
        : `Data for ${missing.join(" and ")} is required.`
      : "",
  };
};

/* Transcribed: accepting the warning drops everything older than five years
   rather than sending it. */
export const trimToFiveYears = (rows, now = new Date()) => {
  const threshold = now.getFullYear() - 5;
  return (rows || []).filter((entry) => entry.year > threshold);
};

/* The eighteen columns the CSV must carry, verbatim and in order. */
export const REQUIRED_CSV_HEADERS = [
  "year",
  "total_revenue",
  "net_earnings",
  "cost_of_goods_serv_sold",
  "earnings_before_interest_tax",
  "interest_expenses",
  "total_equity",
  "total_assets",
  "total_current_assets",
  "inventories",
  "total_current_liabilities",
  "trade_receivables",
  "trade_payables",
  "short_term_debt",
  "long_term_debt",
  "current_long_term_debt_portion",
  "net_cash_flow_operations",
  "net_cash_flow_investment",
];

/* Transcribed: the upload is only allowed once every required column is
   present, matched by exact name. */
export const matchCsvHeaders = (headersInCsv) => {
  const present = headersInCsv || [];
  const matched = [];
  const missing = [];
  REQUIRED_CSV_HEADERS.forEach((header) => {
    if (present.includes(header)) matched.push(header);
    else missing.push(header);
  });
  return { matched, missing };
};

export const findDuplicateYears = (rows) => {
  const seen = new Set();
  const duplicates = new Set();
  (rows || []).forEach((row) => {
    if (seen.has(row?.year)) duplicates.add(row.year);
    seen.add(row?.year);
  });
  return [...duplicates];
};

/* Transcribed from handleContinue: three consecutive years from the newest,
 * nothing five or more years older, and no duplicates. The live messages are
 * kept verbatim because they explain a rule the viewer cannot otherwise guess.
 */
export const CSV_RANGE_MESSAGE =
  "To ensure accuracy and consistency in analysis, we require a minimum of three consecutive years' and a maximum of five years' data. In cases where data for the current year is unavailable, we will utilize the data from the previous year along with the data from the two preceding years to maintain continuity in our analysis.";

export const csvRangeMessageFor = (maxYear) =>
  `Please note that we accept financial data for the last 5 years only. Since you've already uploaded data for the year ${maxYear}, So please remove data for the years ${
    maxYear - 5
  } or earlier.`;

export const validateCsvRows = (rows, { maxYear: recordedMax, now = new Date() } = {}) => {
  const parsed = rows || [];
  if (parsed.length === 0) return { error: "Invalid Data", rows: [] };

  const duplicates = findDuplicateYears(parsed);
  if (duplicates.length > 0) return { error: "Duplicate year detected", rows: [] };

  let maxYear = recordedMax && recordedMax !== 0 ? recordedMax : undefined;
  if (!maxYear) {
    const current = now.getFullYear();
    parsed.forEach((item) => {
      if (item.year === current || item.year === current - 1) {
        maxYear = maxYear ? Math.max(maxYear, item.year) : item.year;
      }
    });
  }
  if (!maxYear) return { error: "Invalid Data", rows: [] };

  const minYear = maxYear - 5;
  const consecutive = parsed.filter(
    (item) => item.year === maxYear || item.year === maxYear - 1 || item.year === maxYear - 2
  );
  const tooOld = parsed.filter((item) => item.year <= maxYear - 5);
  const inRange = parsed.filter((item) => item.year <= maxYear && item.year > minYear);

  if (inRange.length === parsed.length && consecutive.length === 3 && tooOld.length === 0) {
    return { error: "", rows: inRange };
  }

  return {
    error: recordedMax ? csvRangeMessageFor(maxYear) : CSV_RANGE_MESSAGE,
    rows: [],
  };
};

/* The manual form's eighteen figures, grouped as the design draws them. */
export const MANUAL_GROUPS = [
  {
    key: "income",
    title: "Income Statement",
    fields: [
      { key: "total_revenue", label: "Total Revenue" },
      { key: "net_earnings", label: "Net Earning" },
      { key: "cost_of_goods_serv_sold", label: "Cost of Goods / Service Sold" },
      { key: "earnings_before_interest_tax", label: "Earning Before Interest Tax" },
      { key: "interest_expenses", label: "Interest Expenses" },
    ],
  },
  {
    key: "balance",
    title: "Balance Sheet",
    fields: [
      { key: "total_equity", label: "Total Equity" },
      { key: "total_assets", label: "Total Assets" },
      { key: "total_current_assets", label: "Total Current Assets" },
      { key: "inventories", label: "Inventories" },
      { key: "total_current_liabilities", label: "Total Current Liabilities" },
      { key: "trade_receivables", label: "Trade Receivables" },
      { key: "trade_payables", label: "Trade Payables" },
      { key: "short_term_debt", label: "Short Term Debt" },
      { key: "long_term_debt", label: "Long Term Debt" },
      { key: "current_long_term_debt_portion", label: "Current Long Term Debt Portion" },
    ],
  },
  {
    key: "cashflow",
    title: "Cash Flow Statement",
    fields: [
      { key: "net_cash_flow_investment", label: "Net Cash Flow Investment" },
      { key: "net_cash_flow_operations", label: "Net Cash Flow Operation" },
    ],
  },
];

export const MANUAL_FIELD_KEYS = MANUAL_GROUPS.flatMap((g) =>
  g.fields.map((f) => f.key)
);

/* Transcribed: an empty string is sent as null, not as "". */
export const valueOrNull = (value) => {
  const text = String(value ?? "").trim();
  return text === "" ? null : text;
};

/* Transcribed: a year whose every figure is blank is refused with "Form
   cannot be empty" rather than written as a row of nulls. */
export const hasAnyFigure = (values) =>
  MANUAL_FIELD_KEYS.some((key) => String(values?.[key] ?? "").trim() !== "");

/* One year's row, in the shape uploadFinanceDocument takes. */
export const manualPayload = (year, values) => {
  const row = { year: String(year) };
  MANUAL_FIELD_KEYS.forEach((key) => {
    row[key] = valueOrNull(values?.[key]);
  });
  return row;
};
